import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { setActiveTab } from "../redux/slices/projectsSlice";
import { apiGet } from "../services/api/apiClient";
import { SearchInput, Tag } from "../ui";
import HeroBrandLogos from "./HeroBrandLogos";

import "./PortalHero.css";

type ResultKind = "Invoice" | "Document" | "Payment" | "Team";

type SearchResult = {
  id: string;
  kind: ResultKind;
  title: string;
  subtitle?: string;
  to: string;
};

const fmtMoney = (value: unknown) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return "";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const asRows = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

function flattenTeam(projects: any[]) {
  const members: any[] = [];
  const seen = new Set<string>();
  projects.forEach((project) => {
    Object.entries(project?.team || {}).forEach(([category, list]) => {
      (Array.isArray(list) ? list : [list]).forEach((member: any) => {
        const key = member?.id || `${project?.id}-${member?.email}-${member?.fullName}`;
        if (!member || seen.has(key)) return;
        seen.add(key);
        members.push({
          ...member,
          fullName: member.fullName || member.fullname || member.name,
          category,
          projectId: project?.id,
          projectName: project?.name,
        });
      });
    });
  });
  return members;
}

export default function PortalHero({
  eyebrow,
  title,
  summary,
  width = "content",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  summary?: ReactNode;
  /** `content` lines up with the 1100px page column; `full` with the shell. */
  width?: "content" | "full";
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.login);
  const { projects, selectedProject } = useAppSelector((state) => state.projects);

  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const hasProjects = Array.isArray(projects) && projects.length > 0;
  const activeProject: any = selectedProject || projects?.[0] || null;
  const teamMembers = useMemo(() => flattenTeam(projects || []), [projects]);

  // A user can keep a link to a project they are no longer viewing, and the
  // old link can still be readable through the API. Search speaks only for
  // the project on screen.
  const inActiveProject = (row: any) => {
    if (!activeProject) return true;
    const wantedIDs = [activeProject.id, activeProject.providerId, activeProject._id]
      .filter(Boolean)
      .map(String);
    const rowIDs = [row?.projectId, row?.databaseId, row?.customerId, row?.providerId]
      .filter(Boolean)
      .map(String);
    if (wantedIDs.length && rowIDs.some((id) => wantedIDs.includes(id))) return true;
    const wantedNames = [activeProject.name].filter(Boolean).map(String);
    return [row?.projectName, row?.customerName]
      .filter(Boolean)
      .map(String)
      .some((name) => wantedNames.includes(name));
  };

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[] | null>(null);
  const [users, setUsers] = useState<any[] | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Payments and the admin's team list are fetched once, then filtered
  // locally: the payments endpoint reads live Intuit/Bill data, and the users
  // list is the same one the Users page shows.
  useEffect(() => {
    if (query.trim().length < 2) return;

    let alive = true;
    if (!isAdmin && hasProjects && payments === null && user?.id) {
      apiGet({
        endpoint: "/get-payments",
        params: { userId: user.id },
        withAuth: true,
      })
        .then((data: any) => alive && setPayments(Array.isArray(data) ? data : []))
        .catch(() => alive && setPayments([]));
    }

    if (isAdmin && users === null) {
      apiGet({
        endpoint: "/get-users-by-filters",
        params: { profileStatuses: "active,inactive,pendingActivation" },
        withAuth: true,
      })
        .then((data: any) => alive && setUsers(asRows(data)))
        .catch(() => alive && setUsers([]));
    }

    return () => {
      alive = false;
    };
  }, [query, isAdmin, hasProjects, payments, users, user?.id]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(async () => {
      const needle = term.toLowerCase();
      const auth = { withAuth: true } as const;
      const invoiceBase = { userId: user?.id, page: 1, pageSize: 5 };

      const [byNumber, byCustomer, documents] = await Promise.all([
        apiGet({
          endpoint: "/get-invoices",
          params: { ...invoiceBase, invoiceNumber: term },
          ...auth,
        }).catch(() => null),
        apiGet({
          endpoint: "/get-invoices",
          params: { ...invoiceBase, customerName: term },
          ...auth,
        }).catch(() => null),
        activeProject?.id
          ? apiGet({
              endpoint: "/document-library/search",
              params: { projectId: activeProject.id, q: term, limit: 5 },
              ...auth,
            }).catch(() => null)
          : isAdmin
            ? apiGet({
                endpoint: "/search-dropbox",
                params: { q: term, limit: 5, type: "file" },
                ...auth,
              }).catch(() => null)
            : Promise.resolve(null),
      ]);

      if (cancelled) return;

      const invoiceRows = [...asRows(byNumber), ...asRows(byCustomer)].filter(
        inActiveProject,
      );
      const invoiceSeen = new Set<string>();
      const invoiceResults: SearchResult[] = [];
      invoiceRows.forEach((row: any) => {
        const key = String(row.id || row.invoiceNumber || row.number || "");
        if (!key || invoiceSeen.has(key)) return;
        invoiceSeen.add(key);
        const amount = fmtMoney(row.amount ?? row.balance ?? row.amountDue);
        invoiceResults.push({
          id: `invoice-${key}`,
          kind: "Invoice",
          title: [row.invoiceNumber || row.number || "Invoice", amount]
            .filter(Boolean)
            .join(" · "),
          subtitle: [row.customerName, row.projectName, row.paymentStatusLabel]
            .filter(Boolean)
            .join(" · "),
          to: isAdmin ? "/dashboard/invoices" : "/project-home/financial",
        });
      });

      const documentResults: SearchResult[] = asRows(documents)
        .slice(0, 5)
        .map((doc: any, index: number) => ({
          id: `document-${doc._id || doc.id || doc.dropbox_id || index}`,
          kind: "Document",
          title:
            doc.displayName ||
            doc.name ||
            doc.path_display ||
            doc.path ||
            "Document",
          subtitle:
            !isAdmin && doc.sectionLabel
              ? [doc.sectionLabel, doc.groupLabel].filter(Boolean).join(" · ")
              : "",
          to: isAdmin
            ? "/dashboard/dropbox-folders"
            : doc.sectionKey && doc.id
              ? `/project-home/documents?section=${encodeURIComponent(doc.sectionKey)}&item=${encodeURIComponent(doc.id)}`
              : "/project-home/documents",
        }));

      const paymentResults: SearchResult[] = (payments || [])
        .filter(inActiveProject)
        .filter((payment: any) => JSON.stringify(payment).toLowerCase().includes(needle))
        .slice(0, 4)
        .map((payment: any, index: number) => ({
          id: `payment-${payment.id || payment.paymentId || index}`,
          kind: "Payment",
          title: [payment.projectName || "Payment", fmtMoney(payment.amount)]
            .filter(Boolean)
            .join(" · "),
          subtitle: [
            payment.invoiceNumber,
            payment.paymentDate || payment.date,
            payment.status,
          ]
            .filter(Boolean)
            .join(" · "),
          to: "/project-home/financial",
        }));

      const teamPool = isAdmin
        ? users || []
        : teamMembers.filter(
            (member: any) =>
              !activeProject ||
              member.projectId === activeProject.id ||
              member.projectName === activeProject.name,
          );
      const teamResults: SearchResult[] = teamPool
        .filter((member: any) =>
          [member.fullName, member.email, member.position, member.category, member.projectName]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle),
        )
        .slice(0, 4)
        .map((member: any, index: number) => ({
          id: `team-${member.id || member.email || index}`,
          kind: "Team",
          title: member.fullName || member.email || "Team member",
          subtitle: [
            member.position,
            member.email,
            member.projectName,
          ]
            .filter(Boolean)
            .join(" · "),
          to: isAdmin ? "/dashboard/users" : "/project-home",
        }));

      if (cancelled) return;
      setResults([
        ...invoiceResults.slice(0, 5),
        ...documentResults,
        ...paymentResults,
        ...teamResults,
      ]);
      setLoading(false);
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    query,
    isAdmin,
    user?.id,
    payments,
    users,
    teamMembers,
    activeProject?.id,
    activeProject?.name,
  ]);

  const searchable = query.trim().length >= 2;

  return (
    <section className="portal-hero">
      <div className="portal-hero__media" aria-hidden>
        {[
          "/brands/pmh-photo.webp",
          "/brands/cd-photo.webp",
          "/brands/ca-photo.webp",
          "/brands/bhh-photo.webp",
        ].map((src) => (
          <img key={src} src={src} alt="" />
        ))}
      </div>
      <div className="portal-hero__shade" aria-hidden />

      <div
        className={`portal-hero__inner${
          width === "full" ? " portal-hero__inner--full" : ""
        }`}
      >
        <div className="portal-hero__brand">
          <HeroBrandLogos />
        </div>

        {eyebrow && <div className="portal-hero__eyebrow">{eyebrow}</div>}
        <h1 className="portal-hero__title">{title}</h1>
        {summary && <p className="portal-hero__summary">{summary}</p>}

        <div className="portal-search" ref={rootRef}>
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setQuery("");
            }}
            placeholder="Search invoices, documents, payments, team…"
            aria-label="Search the portal"
            autoComplete="off"
          />

          {searchable && (
            <div className="portal-search__results">
              {loading && <div className="portal-search__state">Searching…</div>}
              {!loading && results.length === 0 && (
                <div className="portal-search__state">
                  No matches for “{query.trim()}”.
                </div>
              )}
              {!loading &&
                results.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    data-kind={result.kind}
                    className="portal-search__result"
                    onClick={() => {
                      setQuery("");
                      if (!isAdmin && result.kind === "Invoice") dispatch(setActiveTab(1));
                      if (!isAdmin && result.kind === "Payment") dispatch(setActiveTab(2));
                      navigate(result.to);
                    }}
                  >
                    <Tag tone={result.kind === "Payment" ? "brand" : "neutral"}>
                      {result.kind}
                    </Tag>
                    <span className="portal-search__text">
                      <span className="portal-search__title">{result.title}</span>
                      {result.subtitle && (
                        <span className="portal-search__sub">{result.subtitle}</span>
                      )}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
